import type { AppTranslationKeys } from '@@fire-b2b/i18n/locales/translations.types'
import { logGQLError, type GQLResultError } from '@fire-b2b/utils/gql'

export type RouteErrorOptions = {
  /**
   * Optional custom error message.
   */
  message?: string

  /**
   * Optional translation key for the error message.
   * If provided, this will override the `message` option.
   */
  messageKey?: AppTranslationKeys

  /**
   * Optional debug message to log as an error to the console.
   */
  debugMessage?: string

  /**
   * If true, the error will be displayed on the Nuxt error page.
   * If false, the error will not be displayed on the Nuxt error page.
   *
   * @default true
   */
  fatal?: boolean
}

/**
 * Creates a generic route error with status code 500.
 */
export function routeErrorGeneric(options?: RouteErrorOptions) {
  return routeErrorCustom({
    statusCode: 500,
    messageKey: options?.message ? undefined : 'error.genericError',
    ...options,
  })
}

/**
 * Creates a bad request route error with status code 400.
 */
export function routeErrorBadRequest(options?: RouteErrorOptions) {
  return routeErrorCustom({
    statusCode: 400,
    messageKey: options?.message ? undefined : 'error.invalidUrl',
    ...options,
  })
}

/**
 * Creates a not found route error with status code 404.
 */
export function routeErrorNotFound(options?: RouteErrorOptions) {
  return routeErrorCustom({
    statusCode: 404,
    messageKey: options?.message ? undefined : 'error.pageNotFound',
    ...options,
  })
}

/**
 * Creates a route error based on a `GQLResult<T>` error.
 */
export function routeErrorFromGQL(err: Err<GQLResultError>) {
  logGQLError(err)
  return routeErrorCustom({
    statusCode: err.error.statusCode,
    message: err.error.message,
  })
}

/**
 * Creates a route error based on a `RouteResult<T>` error.
 */
export type RouteResult<T> = Result<T, RouteErrorCustomOptions>
export function routeErrorFromResult(err: Err<RouteErrorCustomOptions>) {
  return routeErrorCustom(err.error)
}

/**
 * Creates a route error with a custom status code and message.
 */
type RouteErrorCustomOptions = RouteErrorOptions & {
  statusCode: number
}
export function routeErrorCustom(options: RouteErrorCustomOptions) {
  const { $i18n } = useNuxtApp()

  // Log debug message to console if provided
  if (options.debugMessage) {
    console.error(options.debugMessage)
  }

  let finalMessage = options.message || $i18n.t('error.genericError')
  if (options.messageKey) {
    finalMessage = $i18n.t(options.messageKey)
  }

  // Return a Nuxt error so the error page can be displayed
  return createError({
    statusCode: options.statusCode,
    statusMessage: finalMessage,
    fatal: options.fatal ?? true,
  })
}
